// offline-session.ts (gh-461) — the credential-free offline identity blob.
//
// A full offline reload loses the memory-only access token (api-client.ts:19, by XSS-hardening
// design), and boot's only restore path is a network refresh that cannot succeed offline. To let a
// clinician RESUME unsynced work after an offline reload without re-login, we persist ONLY the
// decoded NON-SECRET identity claims (the existing AuthUser) + a `lastValidatedAt` stamp to IDB
// `meta` under "auth_session". This is NOT a credential: an XSS script gains no reusable token from
// it, and those claims are already in-session. We NEVER persist any access/refresh/JWT token.
//
// Boot's offline-restore is gated by THREE conditions, all required (see auth-context.tsx):
//   (a) a persisted identity exists,
//   (b) it is within OFFLINE_GRACE_MS of `lastValidatedAt`, AND
//   (c) there is pending offline work on the device (`outbound_queue` non-empty).
// This module owns (b) via `isWithinGrace` and (c) via `hasPendingOfflineWork`.

import type { AuthUser } from "@/contexts/auth-context";

import { db, getMeta, setMeta } from "./idb-schema";
// The single `meta` key holding the offline identity blob. Declared in the pure schema module
// (Story 19.6 Unit E) beside `OWNER_IDENTITY_KEY`, which `owner-scope.ts` needs and cannot reach
// through a Dexie-importing module; a second copy of either literal would be an uncatchable drift,
// and declaring the two side by side is what lets a test pin that they are NOT the same key.
// The v3 `.upgrade()` callback was a third reader until the 19.6 review made that callback a no-op
// (see `idb-schema.ts`); identity lifetime and row ownership are two different facts.
import {
  AUTH_SESSION_KEY,
  SIGNED_OUT_ON_DEVICE_KEY,
  isOwnedBy,
} from "./idb-schema-def";

/**
 * Offline-restore grace window = 12h. Deliberately ≪ the 7-day server refresh TTL
 * (`REFRESH_TOKEN_EXPIRE_DAYS`), so any identity within grace is still backed by a live, rotatable
 * refresh cookie — a session restored within grace WOULD have refreshed successfully online (barring
 * revocation, which reconnect catches). We never resurrect a server-dead session. Security-wise it
 * bounds a lost/shared-device offline window to one shift + overnight. (8h is the conservative
 * alternative.) A named tunable per the spec's Grace Window decision.
 */
export const OFFLINE_GRACE_MS = 12 * 60 * 60 * 1000;

/**
 * Franklin#867 Part A — in-memory latch for `SIGNED_OUT_ON_DEVICE_KEY`.
 *
 * Set/cleared SYNCHRONOUSLY in `markDeviceSignedOut` / `clearDeviceSignedOut` (before any await),
 * mirroring `owner-scope`'s `currentOwner`: a racing silent refresh that lands after sign-out must
 * see the latch in the sync `applyToken` subscriber without waiting on IndexedDB. `null` means
 * "not yet hydrated this page load" — `isDeviceSignedOut()` reads IDB once and fills it.
 *
 * Cross-tab: the same browsing-profile cookie jar is shared, so a sign-out in tab A must flip the
 * latch in tab B (BroadcastChannel) or B keeps an in-memory session until reload.
 */
let deviceSignedOutLatch: boolean | null = null;

/** Channel name — exported so tests can post as a peer tab. */
export const DEVICE_SIGNED_OUT_CHANNEL = "franklin-device-signed-out";

type DeviceSignedOutMessage = { v: 1; signedOut: boolean };

type DeviceSignedOutListener = (signedOut: boolean) => void;

const remoteSignedOutListeners = new Set<DeviceSignedOutListener>();

/** `undefined` = not created yet; `null` = unavailable in this environment. */
let signedOutChannel: BroadcastChannel | null | undefined;

function ensureSignedOutChannel(): BroadcastChannel | null {
  if (signedOutChannel !== undefined) return signedOutChannel;
  if (typeof BroadcastChannel === "undefined") {
    signedOutChannel = null;
    return null;
  }
  try {
    const ch = new BroadcastChannel(DEVICE_SIGNED_OUT_CHANNEL);
    ch.onmessage = (ev: MessageEvent<DeviceSignedOutMessage>) => {
      const data = ev.data;
      if (!data || data.v !== 1 || typeof data.signedOut !== "boolean") return;
      // Peer tab already wrote/cleared IDB; only sync the latch and notify auth.
      deviceSignedOutLatch = data.signedOut;
      for (const listener of remoteSignedOutListeners) {
        listener(data.signedOut);
      }
    };
    signedOutChannel = ch;
  } catch {
    signedOutChannel = null;
  }
  return signedOutChannel;
}

function broadcastDeviceSignedOut(signedOut: boolean): void {
  try {
    ensureSignedOutChannel()?.postMessage({
      v: 1,
      signedOut,
    } satisfies DeviceSignedOutMessage);
  } catch {
    // Best-effort — same-tab latch still holds.
  }
}

/**
 * Subscribe to signed-out marker changes from OTHER tabs (BroadcastChannel).
 * Same-tab mark/clear do not notify — callers already know. Returns an unsubscribe.
 */
export function subscribeDeviceSignedOut(listener: DeviceSignedOutListener): () => void {
  ensureSignedOutChannel();
  remoteSignedOutListeners.add(listener);
  return () => {
    remoteSignedOutListeners.delete(listener);
  };
}

/** The persisted blob: non-secret decoded claims + when they were last network-validated. No token. */
export interface OfflineSession {
  user: AuthUser;
  lastValidatedAt: number;
}

/**
 * Stamp the offline identity. Called from `applyToken(token)` (the single network-validated
 * chokepoint — login/forced-change setSession + every successful silent refresh) with `Date.now()`.
 * Best-effort: an IDB failure must NEVER break the live auth flow, so errors are swallowed.
 */
export async function persistOfflineIdentity(user: AuthUser, now: number): Promise<void> {
  try {
    await setMeta(AUTH_SESSION_KEY, { user, lastValidatedAt: now } satisfies OfflineSession);
  } catch {
    // Best-effort — a failed persist just means no offline-restore next reload, not a broken session.
  }
}

/** Read the persisted offline identity; `undefined` if none. */
export function readOfflineIdentity(): Promise<OfflineSession | undefined> {
  return getMeta<OfflineSession>(AUTH_SESSION_KEY);
}

/**
 * Remove the persisted identity. Called ONLY on genuine expiry (`!res.ok`) or explicit sign-out —
 * NEVER on a transport failure (that would re-break #461: a brief offline blip would wipe the
 * identity and log the clinician out on the next reload). Best-effort.
 *
 * Story 19.6 Unit E — this deletes the SESSION key and nothing else. It must never grow to delete
 * `meta.owner_identity` (the device identity) or any `drafts`/`outbound_queue`/`cache_metadata` row:
 * ownership keys on the DEVICE identity, never on token validity (AC22), and an expired-but-not-
 * replaced session must still be able to read its own local drafts. Wiping the queue here would
 * additionally disable the very offline-restore this module implements — `hasPendingOfflineWork`
 * below is restore gate (c).
 */
export async function clearOfflineIdentity(): Promise<void> {
  try {
    await db.meta.delete(AUTH_SESSION_KEY);
  } catch {
    // Best-effort.
  }
}

/** True while `now` is within OFFLINE_GRACE_MS of the last network validation (boundary inclusive). */
export function isWithinGrace(lastValidatedAt: number, now: number): boolean {
  return now - lastValidatedAt <= OFFLINE_GRACE_MS;
}

/**
 * The pending-work gate (offline-restore condition c). Deliberately keys off `outbound_queue`, NOT
 * `drafts`: the gh-459 online-GET write-through seeds a `drafts` row even for a fully-synced eval, so
 * "drafts present" would leak restore to devices with nothing actually unsynced. Every queued op IS
 * an un-drained local edit, so a non-empty queue is the tightest signal of real work to protect.
 *
 * DW-905 / Franklin#861 rider — scoped to `owner`. Unit E gave every local record an owner; this
 * read was the one that stayed unscoped, so clinician A's residual ops unlocked a credential-free
 * offline session for clinician B on a shared device. Count only ops `isOwnedBy` matches. A falsy
 * `owner` fails CLOSED (`false`), matching `isOwnedBy`. An IDB read error also fails CLOSED → boot
 * falls through to `/sign-in`.
 */
export async function hasPendingOfflineWork(
  owner: string | undefined,
): Promise<boolean> {
  if (!owner) return false;
  try {
    const ops = await db.outbound_queue.toArray();
    return ops.some((op) => isOwnedBy(op, owner));
  } catch {
    return false;
  }
}

/**
 * Franklin#867 Part A — sync read of the signed-out latch for the `applyToken` subscriber.
 * Returns `true` only when the latch is known-set; `null`/unset reads as not signed-out so a cold
 * boot does not block adoption before `isDeviceSignedOut()` has hydrated from IDB (boot itself
 * awaits that hydrate before calling `refreshAccessToken`).
 */
export function isDeviceSignedOutLatch(): boolean {
  return deviceSignedOutLatch === true;
}

/**
 * Mark an explicit sign-out on this device. Latch first (sync), broadcast to peer tabs, then
 * durable IDB write. Best-effort on IDB — the latch still blocks resurrection for this page load.
 */
export async function markDeviceSignedOut(): Promise<void> {
  deviceSignedOutLatch = true;
  broadcastDeviceSignedOut(true);
  try {
    await setMeta(SIGNED_OUT_ON_DEVICE_KEY, true);
  } catch {
    // Best-effort — latch still holds for this page load; next boot re-reads IDB.
  }
}

/**
 * Clear the signed-out marker. Called from interactive `setSession` BEFORE adopting the token,
 * so login is never blocked by a prior offline sign-out. Latch first (sync), broadcast, then IDB.
 */
export async function clearDeviceSignedOut(): Promise<void> {
  deviceSignedOutLatch = false;
  broadcastDeviceSignedOut(false);
  try {
    await db.meta.delete(SIGNED_OUT_ON_DEVICE_KEY);
  } catch {
    // Best-effort — latch is already clear for this page load.
  }
}

/**
 * True when this device was explicitly signed out and nobody has signed in since.
 * Hydrates the in-memory latch from IDB on first read of a page load.
 *
 * Fails CLOSED on IDB read errors (same stance as `hasPendingOfflineWork` / `readOwnerIdentity`):
 * if we cannot read the marker, treat the device as signed-out so a live refresh cookie cannot
 * resurrect a session we may have explicitly ended. Interactive `setSession` still clears the
 * latch synchronously and can log the clinician back in for this page load.
 */
export async function isDeviceSignedOut(): Promise<boolean> {
  if (deviceSignedOutLatch !== null) return deviceSignedOutLatch;
  ensureSignedOutChannel();
  try {
    const stored = await getMeta<unknown>(SIGNED_OUT_ON_DEVICE_KEY);
    deviceSignedOutLatch = stored === true;
  } catch {
    // Fail closed — prefer /sign-in over silent-refresh resurrection when the local store is broken.
    deviceSignedOutLatch = true;
  }
  return deviceSignedOutLatch;
}

/** Test-only: simulate a fresh page load (`null` latch) so IDB hydration can be exercised. */
export function resetDeviceSignedOutLatchForTests(): void {
  deviceSignedOutLatch = null;
}
